import { useEffect, useRef, useState } from 'react';
import { Button, Input, notification } from 'antd';
import { useAppDispatch } from 'store/hooks';
import { createNote, editNote, removeNote } from 'store/slices/notes/actions';
import { useLanguage } from 'core/hooks';
import { INote } from 'types';
import { openNotificationWithIcon } from 'config/notification';
import { NotificationType } from 'store/slices/auth/types';

import './style.scss';

const { TextArea } = Input;

export interface VerseNoteProps {
  note: INote;
  bookNumber: number;
  chapterNumber: number;
  verseIndex: number;
}

export default function VerseNote({
  note,
  bookNumber,
  chapterNumber,
  verseIndex,
}: VerseNoteProps) {
  const dispatch = useAppDispatch();
  const { currentLanguage } = useLanguage();
  const [notificationApi, notificationContextHolder] = notification.useNotification();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isExpanded, setIsExpanded] = useState(Boolean(note?.text?.trim()));
  const [text, setText] = useState(note?.text ?? '');

  const hasNote = Boolean(note?._id);
  const isEmpty = !text.trim();

  useEffect(() => {
    setText(note?.text ?? '');
    if (note?.text?.trim()) setIsExpanded(true);
  }, [note?.text, note?._id]);

  const showSuccessToast = (description?: string) => {
    openNotificationWithIcon({
      api: notificationApi,
      type: NotificationType.success,
      description,
      placement: 'top',
    });
  };

  const save = () => {
    const trimmed = text.trim();
    if (trimmed) {
      if (hasNote && note?._id) {
        dispatch(editNote({
          _id: note._id,
          text: trimmed,
          bookNumber,
          chapterNumber,
          verseIndex,
        }));
        showSuccessToast(currentLanguage.edited_note);
      } else {
        dispatch(createNote({
          text: trimmed,
          verseIndex,
          bookNumber,
          chapterNumber,
        }));
        showSuccessToast(currentLanguage.added_note);
      }
    } else if (hasNote && note?._id) {
      dispatch(removeNote(note._id));
      showSuccessToast(currentLanguage.removed_note);
    }
  };

  const handleBlur = () => {
    save();
    if (isEmpty) setIsExpanded(false);
  };

  const handleRemove = () => {
    if (note?._id) {
      dispatch(removeNote(note._id));
      showSuccessToast(currentLanguage.removed_note);
    }

    setText('');
    setIsExpanded(false);
  };

  const focusTextarea = () => {
    setIsExpanded(true);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  if (!isExpanded) {
    return (
      <>
        {notificationContextHolder}
        <Button
          type="dashed"
          className="verse-note verse-note--placeholder"
          onClick={focusTextarea}
          block
        >
          <span className="verse-note__placeholder-text">
            {currentLanguage.notesTextareaPlaceholder}
          </span>
        </Button>
      </>
    );
  }

  return (
    <>
      {notificationContextHolder}
      <div className="verse-note verse-note--expanded">
        <TextArea
          ref={textareaRef}
          className="verse-note__input"
          placeholder={currentLanguage.notesTextareaPlaceholder}
          value={text}
          onChange={e => setText(e.target.value)}
          onBlur={handleBlur}
          rows={2}
          autoSize={{ minRows: 2, maxRows: 6 }}
        />
        <div className="verse-note__actions">
          {hasNote && (
            <Button
              type="default"
              size="small"
              className="verse-note__btn verse-note__btn--remove"
              onClick={handleRemove}
            >
              {currentLanguage.remove}
            </Button>
          )}
          <Button
            type="primary"
            size="small"
            className="verse-note__btn verse-note__btn--save"
            onMouseDown={e => {
              e.preventDefault();
              save();
            }}
          >
            {currentLanguage.save}
          </Button>
        </div>
      </div>
    </>
  );
}
